import {memo, type ReactNode} from 'react';
import Link from 'next/link';
import IconTile from '@/components/atoms/IconTile';
import Card from '@/components/molecules/Card';
import styles from './IconCard.module.scss';

interface IconCardProps {
    heading: string;
    /** Content of the icon tile (an image or a number). `undefined` renders no tile at all. */
    icon?: ReactNode;
    /** Makes the heading a link. */
    href?: string;
    /** Open the heading link in a new tab. */
    newTab?: boolean;
    /** `stacked`: tile above the heading. `inline`: tile and heading side by side. */
    layout?: 'stacked' | 'inline';
    /** Card fills navy on hover (see Card). */
    blueHover?: boolean;
    /** The card's body: text, check list, tags, footer. */
    children?: ReactNode;
}

/** A Card that starts with an icon tile and an <h3> heading, as used by the service and benefit grids. */
const IconCard = ({heading, icon, href, newTab = false, layout = 'stacked', blueHover = false, children}: IconCardProps) => {
    const tile = icon === undefined ? null : <IconTile>{icon}</IconTile>;
    const title = href ? (
        <h3 className={styles.heading}>
            <Link
                href={href}
                target={newTab ? '_blank' : undefined}
                rel={newTab ? 'noopener noreferrer' : undefined}
                className="card-redirection-heading font-headers "
            >
                {heading}
            </Link>
        </h3>
    ) : (
        <h3 className={`font-headers ${styles.heading}`}>{heading}</h3>
    );

    return (
        <Card blueHover={blueHover}>
            {layout === 'inline' ? (
                <div className={styles.head}>
                    {tile}
                    {title}
                </div>
            ) : (
                <>
                    {tile}
                    {title}
                </>
            )}
            {children}
        </Card>
    );
};

export default memo(IconCard);
